import Component, { mixins } from 'vue-class-component';

import HttpStatusCode from 'http-status-codes'
import { AxiosError, AxiosResponse } from "axios";

import BaseService from '../global/base/Base.service';

import { ICasSpecialRepondant } from './CasSpecialRepondant.model';
import ROUTES from '@/router/ROUTES';
import { IFicheRepondantFormPayload } from './form/FicheRepondantForm';
import { IFileModel, IFileUploadModel } from '../global/file/File.model';
import { IFicheRepondantModel } from './FicheRepondant.model';
import EventBus from '@/EventBus';

export enum EVENTS {
  AFTER_DELETE = 'fiche-repondant:after-delete'
}

export interface IFicheRepondantAfterDeleteEventPayload {
  idCasRepondantFiche: number;
}

export interface IFicheRepondantFetchResponse {
  casRepondantFiches: Array<IFicheRepondantModel>;
  nombreCasSpeciauxRepondantDisponible: number;
}

@Component
export default class FicheRepondantService extends mixins(BaseService) {
  protected service_ficheRepondantService_fetchList(codeSession: string): Promise<IFicheRepondantFetchResponse> {
    return new Promise((resolve: any, reject: any): void => {
      const query: string = this.service_base_generateQueryFromObject({ codeSession })

      this.$axios.get(`/api/inscriptions-lieu-examen/dossier/fiches-repondant/actives?${query}`)
        .then((response: AxiosResponse) => {
          resolve(response.data.data)
        }).catch((error: AxiosError) => this.handleRequestError(error, reject))
    })
  }

  protected service_ficheRepondantService_fetchAvailableCasSpecial(codeSession: string): Promise<Array<ICasSpecialRepondant>> {
    return new Promise((resolve: any, reject: any): void => {
      const query: string = this.service_base_generateQueryFromObject({ codeSession })

      this.$axios.get(`/api/inscriptions-lieu-examen/dossier/fiches-repondant/disponibles?${query}`)
        .then((response: AxiosResponse) => {
          resolve(response.data.data.casSpeciauxRepondant)
        }).catch((error: AxiosError) => this.handleRequestError(error, reject))
    })
  }

  protected service_ficheRepondantService_create(
    payload: IFicheRepondantFormPayload, 
    codeSession: string, 
    idsCasSpecialRepondant: Array<number>
  ): Promise<Array<ICasSpecialRepondant>> {
    return new Promise((resolve: any, reject: any): void => {
      const data: FormData = this.assembleFormDataFromformPayload(payload)

      data.set('codeSession', codeSession)
      idsCasSpecialRepondant.forEach((id: number, index: number) => {
        data.set(`idsCasSpeciauxRepondant[${index}]`, id.toString())
      })

      this.$axios.post('/api/inscriptions-lieu-examen/dossier/fiches-repondant', data, {
        headers: this.service_baseService_multipartFormDataHeader()
      }).then((response: AxiosResponse) => {
          resolve(response.data.data.casSpeciauxRepondant)
        }).catch((error: AxiosError) => this.handleRequestError(error, reject))
    })
  }

  protected service_ficheRepondantService_update(
    payload: IFicheRepondantFormPayload, 
    idCasRepondantFiche: number
  ): Promise<Array<ICasSpecialRepondant>> {
    return new Promise((resolve: any, reject: any): void => {
      const query: string = this.service_base_generateQueryFromObject({ idCasRepondantFiche })
      const data: FormData = this.assembleFormDataFromformPayload(payload)

      this.$axios.put(`/api/inscriptions-lieu-examen/dossier/fiches-repondant?${query}`, data, {
        headers: this.service_baseService_multipartFormDataHeader()
      }).then((response: AxiosResponse) => {
          resolve(response.data.data.casSpeciauxRepondant)
        }).catch((error: AxiosError) => this.handleRequestError(error, reject))
    })
  }

  protected service_ficheRepondantService_delete(idCasRepondantFiche: number): Promise<void> {
    return new Promise((resolve: any, reject: any): void => {
      const query: string = this.service_base_generateQueryFromObject({ idCasRepondantFiche })

      this.$axios.delete(`/api/inscriptions-lieu-examen/dossier/fiches-repondant?${query}`)
        .then((response: AxiosResponse) => {
          EventBus.$emit(EVENTS.AFTER_DELETE, { idCasRepondantFiche } as IFicheRepondantAfterDeleteEventPayload)

          resolve()
        }).catch((error: AxiosError) => this.handleRequestError(error, reject))
    })
  }

  protected service_ficheRepondantService_fetchById(idCasRepondantFiche: number): Promise<IFicheRepondantModel> {
    return new Promise((resolve: any, reject: any): void => {
      const query: string = this.service_base_generateQueryFromObject({ idCasRepondantFiche })

      this.$axios.get(`/api/inscriptions-lieu-examen/dossier/fiches-repondant?${query}`)
        .then((response: AxiosResponse) => {
          resolve(response.data.data.casRepondantFiche)
        }).catch((error: AxiosError) => this.handleRequestError(error, reject))
    })
  }

  private assembleFormDataFromformPayload(payload: IFicheRepondantFormPayload): FormData {
    const data: FormData = new FormData()

    data.set('ville', payload.ville)
    data.set('idPays', payload.idPays.toString())
    data.set('nom', payload.nom)
    data.set('prenom', payload.prenom)
    data.set('fonction', payload.fonction)
    data.set('telephonePrincipal', payload.telephone || '')
    data.set('courriel', payload.courriel)
    data.set('langue', payload.langue)
    data.set('employeurNom', payload.employeurNom)
    data.set('employeurAdresse', payload.employeurAdresse || '')
    data.set('employeurPreuveUrl', payload.employeurPreuveUrl || '')
    
    payload.employeurPreuveFichiers.forEach((uploadedFile: IFileUploadModel<IFileModel>, index: number) => {
      if (uploadedFile.server_file) {
        data.set(`fichiersForms[${index}].id`, uploadedFile.server_file.id)
      } else if (uploadedFile && uploadedFile.file) {
        data.append(`fichiersForms[${index}].file`, uploadedFile.file.file)
      }
    })

    return data
  }

  private handleRequestError(error: AxiosError, reject: any): void {
    let status: string = HttpStatusCode.INTERNAL_SERVER_ERROR.toString()

    if (error.response) {
      status = error.response?.status.toString()
    }

    if (status === HttpStatusCode.UNAUTHORIZED.toString()) {
      this.mixin_router_navigate({
        name: ROUTES.LOGIN,
      })
    } else {
      this.mixin_router_navigate({
        name: ROUTES.ERROR,
        params: {
          status
        }
      })
    }

    reject(error)
  }
}